/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import {
    AfterContentChecked,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    OnDestroy,
    OnInit
} from '@angular/core';
import { DeviceService } from '../../shared/services/device.service';
import { FeatureReportCollection, ReportSubtypeValue } from '../state/report.model';
import { LoggerService } from '../../shared/services/logger.service';
import { ReportService } from '../services/report.service';
import { QueryService } from '../../map/services/query.service';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil, tap } from 'rxjs/operators';
import { DashGisUtil } from '../../shared/dash-gis-util';
import { MapConfigService } from '../../map/services/mapConfig.service';
import { RedlineConfigService } from '../../../components/redline/services/redline-config.service';
import { MatDialog } from '@angular/material/dialog';
import { Actions, Select, Store } from '@ngxs/store';
import { ReportDisplayOverlayRef } from './report-display.overlay-ref';
import { ReportState } from '../state/report.state';
import { ReportDisplayBaseComponent } from './report-display-base.component';
import { isEqual } from 'lodash-es';
import { SelectionState } from '../../map/state/selection.state';
import { SelectionFeatures } from '../../map/state/selection.model';
import { ResetReportPagingAndSort } from '../state/report.actions';

@Component({
    selector: 'dash-gis-report-display',
    templateUrl: './feature-report-display.component.html',
    styleUrls: ['./feature-report-display.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FeatureReportDisplayComponent
    extends ReportDisplayBaseComponent
    implements OnInit, AfterContentChecked, OnDestroy {

    @Select(ReportState.activeFeatureReports) activeFeatureReports$: Observable<FeatureReportCollection>;
    @Select(SelectionState.selection) selection$: Observable<SelectionFeatures>;

    activeLayers$: Observable<string[]>;
    initialWidth$ = new Subject<number>();
    activeLayer$ = new BehaviorSubject<string>('');
    shouldDisableScroll$: Observable<boolean>;

    protected historyGroup = 'featureReports';

    private overrideSubtype$ = new BehaviorSubject<ReportSubtypeValue>(null);

    public ngOnInit() {
        super.ngOnInit();
        this.initialWidth$.pipe(takeUntil(this.dispose$)).subscribe((width) => {
            if (this.overlayRef.hostElement) {
                this.panelWidthSubject.next(`${ width }px`);
                this.overlayRef.updateSize({ width: `${ width }px` });
                this.overlayRef.updatePosition();
                this.cdr.detectChanges();
            }
        });

        this.activeFeatureReports$.pipe(takeUntil(this.dispose$)).subscribe((reports) => {
            if (Object.keys(reports).length === 0) {
                this.closePanel.emit();
            }
        });

        this.selection$
            .pipe(distinctUntilChanged(isEqual), takeUntil(this.dispose$))
            .subscribe(() => this.store.dispatch(new ResetReportPagingAndSort({ id: this.uid })));
    }

    public ngAfterContentChecked() {
        super.ngAfterContentChecked();
    }

    public ngOnDestroy() {
        super.ngOnDestroy();
    }

    public constructor(
        logger: LoggerService,
        mapConfig: MapConfigService,
        redlineConfig: RedlineConfigService,
        reportService: ReportService,
        queryService: QueryService,
        deviceService: DeviceService,
        dialog: MatDialog,
        actions$: Actions,
        store: Store,
        cdr: ChangeDetectorRef,
        @Inject(ReportDisplayOverlayRef) overlayRef: ReportDisplayOverlayRef
    ) {
        super(
            logger,
            mapConfig,
            redlineConfig,
            reportService,
            queryService,
            deviceService,
            dialog,
            actions$,
            store,
            cdr,
            overlayRef
        );

        this.activeLayers$ = this.activeFeatureReports$.pipe(
            distinctUntilChanged(isEqual),
            map((activeReports) => Object.keys(activeReports)),
        );

        this.shouldDisableScroll$ = this.activeLayers$.pipe(
            map(layers => layers.length > 1)
        );

        combineLatest([
            this.activeFeatureReports$,
            this.activeLayer$,
            this.overrideSubtype$,
        ]).pipe(
            filter(([r, l, overrideSubtype]) => overrideSubtype !== 'pending'),
            map(([ reports, layer, overrideSubtype ]) => {
                let isDetail = false;
                const layers = Object.getOwnPropertyNames(reports);
                if (DashGisUtil.isObject(reports)) {
                    let layerReports;
                    if (!layer) {
                        layerReports = reports[layers[0]];
                    } else {
                        layerReports = reports[layer];
                    }
                    if (layerReports && layerReports.length === 1) {
                        const subtype = overrideSubtype || layerReports[0].definition.subType;
                        isDetail = subtype === 'detail' || subtype === 'pending';
                    }
                }
                return (isDetail ? 450 : 900) + (layers.length > 1 ? 170 : 0);
            }),
            tap((width) => this.initialWidth$.next(width))
        ).subscribe();
    }

    setActiveLayer(layerName: string) {
        this.activeLayer$.next(layerName);
    }

    setSubtype(overrideSubtype: ReportSubtypeValue) {
        this.overrideSubtype$.next(overrideSubtype);
    }
}
